import { fmtCents } from '@/components/money/format.js';

// THE TRUST CARD IN PLAIN WORDS (NIGHT-13 step 2, David 2026-11-02): cash, what is owed to carriers and on which
// statement by when, what is owed between accounts, and what is left over, explained line by line. Every figure is the
// server's (utils/trust-card-words.js over the §4.4 number); this screen derives nothing and adds nothing up.

const deals = (list) => list.map((d) => `${d.deal}${d.amount === null || d.amount === undefined ? ' (amount not known)' : ` ${fmtCents(d.amount)}`}`).join(' · ');
const dueWords = (g) => (g.due_date ? `due ${g.due_date}` : 'no due date on the statement');

function Line({ label, amount, children, danger = false }) {
  return (
    <li className="py-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className={danger ? 'text-danger' : 'text-text'}>{label}</span>
        {amount === undefined ? null : <span className={`font-mono ${danger ? 'text-danger' : 'text-text'}`}>{amount === null ? 'not known' : fmtCents(amount)}</span>}
      </div>
      {children ? <div className="mt-0.5 text-xs text-text-muted">{children}</div> : null}
    </li>
  );
}

export default function TrustCardWords({ w }) {
  if (!w) return null;
  const o = w.owed_to_carriers;
  return (
    <div className="mt-3 rounded-card border border-border bg-bg p-3 text-sm">
      <ul className="divide-y divide-border">
        <Line label="Owed to carriers from this account" amount={o.amount} danger={o.provisional}>
          <ul className="mt-1 space-y-0.5">
            {o.on_statements.map((g) => <li key={g.statement}>{g.statement}, {dueWords(g)}: <span className="font-mono">{fmtCents(g.amount)}</span>{g.unknown ? ` and ${g.unknown} not known` : ''} ({deals(g.deals)})</li>)}
            {o.paid_waiting.map((g) => <li key={`${g.carrier_payment_id}-${g.statement}`}>Already paid {g.paid_on} on {g.statement}, not yet settled: <span className="font-mono">{fmtCents(g.amount)}</span> ({deals(g.deals)}). {g.ready ? 'Settle it on the statement.' : `Settle it once ${g.why}.`}</li>)}
            {o.paid_unknown.map((g) => <li key={g.statement}>{g.statement} is paid, but no payment says which deals: <span className="font-mono">{fmtCents(g.amount)}</span> ({deals(g.deals)}).</li>)}
            {o.after_payment.deals.length ? <li>Still owed after what was paid against them: <span className="font-mono">{fmtCents(o.after_payment.amount)}</span> ({deals(o.after_payment.deals)}).</li> : null}
            {o.not_billed.deals.length ? <li>Collected, on no confirmed statement line yet: <span className="font-mono">{fmtCents(o.not_billed.amount)}</span> across {o.not_billed.deals.length} deal{o.not_billed.deals.length === 1 ? '' : 's'}{o.not_billed.unknown ? `, ${o.not_billed.unknown} of them with the amount owed not known (no rate stated)` : ''}.</li> : null}
          </ul>
        </Line>
        {w.outside_number.deals.length ? (
          <Line label="Also owed from this account, outside the trust number" amount={w.outside_number.amount} danger>
            Collected before the money system began (8/7), so the number below leaves them out: {w.outside_number.deals.map((d) => `${d.deal} ${fmtCents(d.amount)} on ${d.statement}${d.due_date ? `, due ${d.due_date}` : ''}`).join(' · ')}.
          </Line>
        ) : null}
        {w.between.map((g) => (
          <Line key={`${g.direction}-${g.other}`} label={g.direction === 'owes' ? `Owes ${g.other}` : `Is owed by ${g.other}`} amount={g.amount}>{deals(g.deals)}</Line>
        ))}
        {w.other.map((t) => <Line key={t.label} label={t.label} amount={t.amount} />)}
        <Line label="Left over: the agency's money in this account" amount={w.left_over.amount} danger={w.left_over.provisional || (w.left_over.amount !== null && w.left_over.amount < 0)}>
          {w.unsettled_payments && w.unsettled_payments.payments.length ? (
            <div className="mb-1 text-danger">
              Paid from this account and not yet settled against the deals it paid: <span className="font-mono">{fmtCents(w.unsettled_payments.amount)}</span> in {w.unsettled_payments.payments.length} payment{w.unsettled_payments.payments.length === 1 ? '' : 's'}. Until each is settled, the deals it paid still read owed, so what is left over cannot be explained.
              <ul className="mt-0.5 space-y-0.5 text-text-muted">
                {w.unsettled_payments.payments.map((p) => <li key={p.carrier_payment_id}>{p.paid_on}{p.payee ? ` to ${p.payee}` : ''}: <span className="font-mono">{fmtCents(p.amount)}</span>{p.reference ? ` (${p.reference})` : ''}: {p.ready ? 'ready to settle on Statements' : p.why}</li>)}
              </ul>
            </div>
          ) : null}
          {w.explained ? (
            <>
              <div>Line by line, what the sweep queue says is the agency&apos;s here:</div>
              <ul className="mt-0.5 space-y-0.5">
                {w.explained.rows.map((r, i) => <li key={i}>{r.deal}{r.received_on ? `, received ${r.received_on}` : ''}: {r.amount === null ? <span className="text-danger">not known ({r.why})</span> : <span className="font-mono">{fmtCents(r.amount)}</span>}</li>)}
                {w.explained.owed_back ? <li>less what this account is owed back from the sweep account: <span className="font-mono">{fmtCents(-w.explained.owed_back)}</span></li> : null}
              </ul>
              <div className="mt-0.5">Explained: <span className="font-mono">{fmtCents(w.explained.total)}</span>{w.explained.unknown ? `, with ${w.explained.unknown} not known` : ''}.{' '}
                {w.explained.difference === null ? null : w.explained.difference === 0
                  ? 'That is all of it.'
                  : <span className="text-danger">Not explained: <span className="font-mono">{fmtCents(w.explained.difference)}</span>. That is money the card counts as the agency&apos;s that no deposit row accounts for{w.explained.difference < 0 ? ' (the account holds less than it owes)' : ''}.</span>}
              </div>
            </>
          ) : null}
        </Line>
      </ul>
    </div>
  );
}

// Owed to a carrier with no client money behind it (David 2026-11-02: Kestrel Radio and Tobin Ashgrove on SAV 9/30). On no
// card: no trust account holds the money, and no trust account pays it.
export function NoClientMoney({ rows }) {
  if (!rows || !rows.length) return null;
  return (
    <div className="mt-4 rounded-card border border-danger/40 bg-surface p-4 text-sm">
      <h3 className="text-sm font-semibold text-danger">Owed to carriers with no client money behind it</h3>
      <ul className="mt-2 space-y-1 text-text">
        {rows.map((r, i) => <li key={i}>{r.deal} <span className="font-mono">{fmtCents(r.amount)}</span>: client has not paid; trust cannot cover it. <span className="text-xs text-text-muted">{r.statement}{r.due_date ? `, due ${r.due_date}` : ''}{r.why ? ` · ${r.why}` : ''}</span></li>)}
      </ul>
    </div>
  );
}
